import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { JSX, ReactNode } from 'react'
import { api, call, onSessionExpired } from '@/api/client'
import { AuthContext } from './context'
import type { AuthValue, Identity } from './context'
import { useIdleSignOut } from './idle'

/**
 * Who is signed in, and what happens when that stops being true.
 *
 * One `GET /auth/me` on mount decides between anonymous and authenticated; the
 * cookie is `HttpOnly`, so asking the server is the *only* way to find out, and
 * a client-side guess would be a guess about a security boundary.
 *
 * The interesting part is expiry. `call()` raises a signal on any 401, and this
 * provider is the single subscriber. On expiry the identity is **kept** and
 * `expired` is set: the user stays on the screen they were on and re-signs-in
 * over it, which is the S-D2 behaviour and the reason the plan calls out
 * "re-auths in place rather than dumping to /login". Dropping straight to
 * anonymous would unmount the whole tree and lose whatever was half-typed.
 *
 * The **idle sign-out is the one exception**, and it is a real sign-out: no
 * activity for fifteen minutes (`useIdleSignOut`) ends the session on the
 * server and drops to anonymous, so the screen a person walked away from is
 * not left on a shared machine behind a password prompt with their draft
 * still visible under it. The login screen says why (`signedOutReason`).
 */
export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [status, setStatus] = useState<AuthValue['status']>('loading')
  const [identity, setIdentity] = useState<Identity | null>(null)
  const [expired, setExpired] = useState(false)
  const [signedOutReason, setSignedOutReason] = useState<AuthValue['signedOutReason']>(null)

  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const load = useCallback(async (signal?: AbortSignal): Promise<void> => {
    const result = await call(() => api.GET('/api/v1/auth/me', signal ? { signal } : {}))
    if (!mounted.current) return
    if (result.ok) {
      setIdentity({ user: result.data.user, session: result.data.session })
      setStatus('authenticated')
      setExpired(false)
    } else {
      // Any failure here means the app cannot act as anybody. A network failure
      // and a 401 differ in cause but not in consequence, and pretending to be
      // signed in because the answer never arrived would put the user in front
      // of screens whose every request then fails.
      setIdentity(null)
      setStatus('anonymous')
      setExpired(false)
    }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    void (async () => {
      try {
        await load(controller.signal)
      } catch {
        // `call()` re-throws only AbortError, which is this effect cleaning up.
      }
    })()
    return () => {
      controller.abort()
    }
  }, [load])

  // The current status, readable from a callback that is not a render.
  // Deliberately a ref rather than reading it inside a `setStatus` updater:
  // calling one setter from inside another's updater is not something React
  // supports, and React 19 discards the nested update — which showed up as an
  // expired session that raised no dialog at all.
  const statusRef = useRef(status)
  statusRef.current = status

  useEffect(
    () =>
      onSessionExpired(() => {
        if (!mounted.current) return
        // Only meaningful for someone who *was* signed in. A 401 from the login
        // form is a wrong password, not an expiry, and raising the re-auth
        // dialog over the login screen would be absurd.
        if (statusRef.current === 'authenticated') setExpired(true)
      }),
    [],
  )

  const adopt = useCallback((next: Identity) => {
    setIdentity(next)
    setStatus('authenticated')
    setExpired(false)
    setSignedOutReason(null)
  }, [])

  const refresh = useCallback(async () => {
    await load()
  }, [load])

  const signOut = useCallback(async () => {
    // The response is not checked. Whatever the server says, this browser is
    // done with the session — a failed logout that left the UI signed in would
    // be the worse outcome, and the cookie is cleared server-side or it is not.
    await call(() => api.POST('/api/v1/auth/logout', {}))
    if (!mounted.current) return
    setIdentity(null)
    setStatus('anonymous')
    setExpired(false)
    setSignedOutReason(null)
  }, [])

  const signOutIdle = useCallback(async () => {
    await signOut()
    if (!mounted.current) return
    // After, not before: `signOut` clears the reason, and this is the one
    // caller with a reason to leave behind.
    setSignedOutReason('idle')
  }, [signOut])

  // Armed only for a live session. Not while the re-auth dialog is up: the
  // server has already refused, and there is nothing left to sign out of.
  useIdleSignOut(status === 'authenticated' && !expired, () => {
    void signOutIdle()
  })

  const value = useMemo<AuthValue>(
    () => ({
      status,
      user: identity?.user ?? null,
      session: identity?.session ?? null,
      expired,
      signedOutReason,
      adopt,
      refresh,
      signOut,
    }),
    [status, identity, expired, signedOutReason, adopt, refresh, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
